import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { DropdownItemProps, Input, Select } from "semantic-ui-react";
import { DateInput } from "../../../components/DateInput/DateInput";
import { TranslateProps } from "../../../i18n/TranslateProps";
import { aplicationStatusOptions } from "../../../models/certificate";
import {
    CertificateType,
    costBearerOptions,
    ICertificate,
    statePrintingCertificates,
} from "../../../models/certificate";
import { deconstructDate, genderProps, getCertificateEndDate, IDate } from "../../../models/common";
import { BoatmasterATInputs } from "./BoatmasterATInputs";
import { ICCorICSInputs } from "./ICCorICSInputs";
import { useSelector } from "react-redux";
import { AppState } from "../../../store/configureStore";
import { UserRole } from "../../../actions/authentificationActions";
import "./CertificateDetailsForm.css";

type Props = {
    certificate: ICertificate;
    setCertificate: Dispatch<SetStateAction<ICertificate>>;
    startDate: IDate;
    setStartDate: Dispatch<SetStateAction<IDate>>;
    endDate: IDate;
    setEndDate: Dispatch<SetStateAction<IDate>>;
    dateOfBirth: string;
    setFitnessStartDate: Dispatch<SetStateAction<IDate>>;
    setFitnessEndDate: Dispatch<SetStateAction<IDate>>;
    renew: boolean;
    availableCertificatesTypes: DropdownItemProps[];
    availableRiverRanges: DropdownItemProps[];
    isUpdate?: boolean;
    setAttachment1?: Dispatch<SetStateAction<File | undefined>>;
    setAttachment2?: Dispatch<SetStateAction<File | undefined>>;
    readonly?: boolean;
};

export const CertificateDetailsForm = ({
    certificate,
    setCertificate,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    dateOfBirth,
    setFitnessStartDate,
    setFitnessEndDate,
    renew,
    availableCertificatesTypes,
    availableRiverRanges,
    isUpdate = false,
    setAttachment1,
    setAttachment2,
    readonly = false,
}: Props) => {
    const intl = useIntl();
    const user = useSelector((state: AppState) => state.user);
    const setCertificateEndDates = useCallback(() => {
        if (isUpdate) {
            setEndDate(deconstructDate(certificate.endDate));
        } else {
            setEndDate(getCertificateEndDate(dateOfBirth, certificate.type));
        }
        setFitnessEndDate(getCertificateEndDate(dateOfBirth, certificate.type));
    }, [dateOfBirth, certificate.type, setEndDate, setFitnessEndDate, isUpdate]);

    useEffect(() => {
        setCertificateEndDates();
    }, [setCertificateEndDates]);

    return (
        <>
            <div className="certificate-row four">
                <section id="certificate-row-span-two-first" className="input-container">
                    <label>
                        {intl.formatMessage({ id: "type" })}
                        <span className="input-required">*</span>
                    </label>
                    <Select
                        search
                        fluid
                        options={availableCertificatesTypes}
                        value={certificate.type}
                        onChange={(e, data: any) => {
                            setCertificate({ ...certificate, type: +data.value });
                        }}
                        disabled={readonly || renew || user.role === UserRole.OfficeUserViadonau}
                    />
                </section>
                {certificate.type ===
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS ? (
                    <>
                        {certificate.qualificationID && (
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "icNumber" })}:</label>
                                <Input value={certificate.qualificationID} disabled />
                            </section>
                        )}
                        {certificate.administrativeNumber && (
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "qualificationID" })}:</label>
                                <Input value={certificate.administrativeNumber} disabled />
                            </section>
                        )}
                    </>
                ) : (
                    <>
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "startDate" })}
                                <span className="input-required">*</span>
                            </label>
                            <DateInput
                                date={startDate}
                                setDate={setStartDate}
                                setAdditionalDate={setFitnessStartDate}
                                extendedYears
                            />
                        </section>

                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "endDate" })}
                                {certificate.type !==
                                    CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt &&
                                    certificate.type !==
                                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraft && (
                                        <span className="input-required">*</span>
                                    )}
                            </label>
                            <DateInput
                                date={endDate}
                                setDate={setEndDate}
                                setAdditionalDate={setFitnessEndDate}
                                disabled={
                                    certificate.type ===
                                    CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt
                                }
                                extendedYears
                                nonUnionCertificatesDate={
                                    certificate.type === CertificateType.EngineersCertificate ||
                                    certificate.type === CertificateType.BoatmastersCertificateAt ||
                                    certificate.type ===
                                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraft ||
                                    certificate.type ===
                                        CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt
                                }
                            />
                        </section>
                    </>
                )}
            </div>
            <div className="certificate-row four">
                <section id="certificate-row-span-two-first" className="input-container">
                    <label>{intl.formatMessage({ id: "comments" })}</label>
                    <Input
                        value={certificate.comments ?? ""}
                        onChange={(e: any) =>
                            setCertificate({
                                ...certificate,
                                comments: e.target.value,
                            })
                        }
                    />
                </section>
                {statePrintingCertificates &&
                    statePrintingCertificates.includes(certificate.type) && (
                        <section className="input-container">
                            <label>
                                {certificate.type ===
                                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
                                    ? intl.formatMessage({ id: "feeType" })
                                    : intl.formatMessage({ id: "costBearer" })}
                                <span className="input-required">*</span>
                            </label>
                            <Select
                                value={certificate.costBearer}
                                options={TranslateProps(costBearerOptions)}
                                onChange={(e, data: any) =>
                                    setCertificate({
                                        ...certificate,
                                        costBearer: +data.value,
                                    })
                                }
                                disabled={readonly}
                            />
                        </section>
                    )}
                {certificate.type === CertificateType.UcqBoatmaster && (
                    <section className="input-container">
                        <label>
                            {intl.formatMessage({ id: "gender" })}
                            <span className="input-required">*</span>
                        </label>
                        <Select
                            value={certificate.gender}
                            options={TranslateProps(genderProps)}
                            onChange={(e, data: any) =>
                                setCertificate({
                                    ...certificate,
                                    gender: +data.value,
                                })
                            }
                        />
                    </section>
                )}
                {certificate.type > CertificateType.UcqPassengerNavigationExpert &&
                    certificate.type !==
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "adminNumber" })}
                                <span className="input-required">*</span>
                            </label>
                            <Input
                                value={certificate.administrativeNumber ?? ""}
                                onChange={(e: any) =>
                                    setCertificate({
                                        ...certificate,
                                        administrativeNumber: e.target.value,
                                    })
                                }
                            />
                        </section>
                    )}
                {certificate.type ===
                    CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt && (
                    <section className="input-container">
                        <label>
                            {intl.formatMessage({ id: "riverKilometers" })}
                            <span className="input-required">*</span>
                        </label>
                        <Select
                            search
                            multiple
                            options={availableRiverRanges}
                            value={certificate.riverKilometers}
                            onChange={(e, data: any) =>
                                setCertificate({ ...certificate, riverKilometers: data.value })
                            }
                        />
                    </section>
                )}
                {certificate.type ===
                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "status" })}:</label>
                        <Input
                            value={
                                TranslateProps(aplicationStatusOptions).find(
                                    x => x.value === certificate.applicationStatus
                                )?.text
                            }
                            disabled
                        />
                    </section>
                )}
            </div>
            {certificate.type === CertificateType.UcqBoatmaster && (
                <div className="certificate-row two">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "restriction96" })}</label>
                        <Input
                            value={certificate.restriction96 ?? ""}
                            onChange={(e: any) =>
                                setCertificate({
                                    ...certificate,
                                    restriction96: e.target.value,
                                })
                            }
                        />
                    </section>
                </div>
            )}
            {(certificate.type ===
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraft ||
                certificate.type ===
                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS) && (
                <ICCorICSInputs
                    certificate={certificate}
                    setCertificate={setCertificate}
                    setAttachment1={setAttachment1}
                    setAttachment2={setAttachment2}
                />
            )}
            {certificate.type === CertificateType.BoatmastersCertificateAt && (
                <BoatmasterATInputs certificate={certificate} setCertificate={setCertificate} />
            )}
        </>
    );
};
